import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import PhaseSuggestionCard from "@/components/PhaseSuggestionCard";
import EmphasisHistoryStrip from "@/components/EmphasisHistoryStrip";
import DriftNudgeCard from "@/components/DriftNudgeCard";
import ReflectionCanvasArticle from "./ReflectionCanvasArticle";
import EmbraceMasthead from "./EmbraceMasthead";
import { useAuth } from "@/lib/auth";

/**
 * Embrace workspace — the "living advisory process" surface inside every
 * engagement (Feb 2026, post-consolidation).
 *
 * Composition, top → bottom:
 *   §I  · Editorial masthead + client cover band
 *   §II · The Yadid Embrace — editorial Method index (Roman I–IV plus
 *         two continuous dynamics). The Method index is the SOLE
 *         selector; tapping any node pins it and opens the Reflection
 *         Canvas beneath the index.
 *   Reflection Canvas — the single integrated strategic workspace for
 *         the pinned node. Houses framing, progressive prompts,
 *         governed Current Reflection, Working Journal (with delete),
 *         Next Questions (movement pins), Signals from the field
 *         (movement pins), Working Notes (lens pins), and Context Layer.
 *   §III · Approved emphasis banner ("Where we're leaning") with
 *         Suggest / Change governance.
 *   Supporting non-blocking cards — pending suggestion, drift nudge.
 *
 * Endpoints referenced by the canvas (unchanged):
 *   PATCH  /engagements/{id}/roadmap/phase/{key}/workspace
 *   POST   /engagements/{id}/roadmap/phase/{key}/reflection
 *   DELETE /engagements/{id}/roadmap/phase/{key}/reflection/{rid}
 *   PATCH  /engagements/{id}/roadmap/phase/{key}/current-reflection
 *   PATCH  /engagements/{id}/roadmap/lens/{lens_key}
 *   POST   /engagements/{id}/roadmap/lens/{lens_key}/reflection
 *   DELETE /engagements/{id}/roadmap/lens/{lens_key}/reflection/{rid}
 *   PATCH  /engagements/{id}/roadmap/lens/{lens_key}/current-reflection
 *   GET    /engagements/{id}/method-signals
 */

export default function EngagementEmbraceTab({
  engagementId, clientId, engagement, roadmap, setRoadmap,
  onOpenEmphasisDialog, canChangeEmphasis,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: authUser } = useAuth();
  const [signals, setSignals] = useState(null);
  const [signalsMovement, setSignalsMovement] = useState(null);   // null = All
  // Pinned detail panel — click a Method index node to reveal the
  // Reflection Canvas beneath the index. Click the same node again,
  // click outside the map region, or press Esc to clear.
  // Shape: { type: 'movement' | 'dynamic', key: string } | null
  //
  // Feb 2026 · Iter 58 — The Reflection Canvas renders as a CHILD of
  // the §II map region (immediately below the Emphasis History strip).
  // Its DOM location does NOT depend on `pinnedNode` — only its
  // presence does. This keeps the outer section tree identical in
  // open and closed states so §III Emphasis and §II Yadid Embrace
  // never move when a phase is selected.
  const [pinnedNode, setPinnedNode] = useState(null);
  const mapRegionRef = useRef(null);

  // Auto-pin the current emphasis on first load so the strategic
  // workspace is immediately visible without requiring a click. If no
  // emphasis is set, the map remains idle and the advisor picks the
  // first node themselves. Guarded so subsequent pin changes don't
  // fight the initial defaulting.
  const defaultPinAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultPinAppliedRef.current) return;
    if (!roadmap) return;
    if (roadmap.current_phase && !pinnedNode) {
      setPinnedNode({ type: "movement", key: roadmap.current_phase });
    }
    defaultPinAppliedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadmap]);

  // Clear pin when the advisor clicks outside the map region. Iter 58
  // — the canvas is again a child of the map region, so a single ref
  // is sufficient. Escape still clears from anywhere.
  useEffect(() => {
    if (!pinnedNode) return;
    const onMouseDown = (ev) => {
      if (mapRegionRef.current && !mapRegionRef.current.contains(ev.target)) {
        setPinnedNode(null);
      }
    };
    const onKey = (ev) => { if (ev.key === "Escape") setPinnedNode(null); };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinnedNode]);

  // Selecting a movement node from the Roman-numeral index. Toggles the
  // pin off when the same node is tapped again.
  const onSelectMovement = (k) => {
    setPinnedNode((prev) =>
      prev && prev.type === "movement" && prev.key === k
        ? null
        : { type: "movement", key: k }
    );
  };
  // Selecting a continuous dynamic — same toggle semantics.
  const onSelectDynamic = (k) => {
    setPinnedNode((prev) =>
      prev && prev.type === "dynamic" && prev.key === k
        ? null
        : { type: "dynamic", key: k }
    );
  };

  // Round-4 deep-link support · `?movement=reveal` or `?dynamic=connections`
  // pins the correct node on mount so the Client workspace, Briefing Studio,
  // or any other doorway can land the advisor directly in the reflection
  // article. Consumed once and stripped from the URL.
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (deepLinkConsumedRef.current) return;
    if (!roadmap) return;
    const mv = (searchParams.get("movement") || "").toLowerCase();
    const dy = (searchParams.get("dynamic") || "").toLowerCase();
    let nextPin = null;
    if (mv && ["listen","reveal","reimagine","guide"].includes(mv)) {
      nextPin = { type: "movement", key: mv };
    } else if (dy && ["connections","leadership"].includes(dy)) {
      nextPin = { type: "dynamic", key: dy };
    }
    if (nextPin) {
      setPinnedNode(nextPin);
      // Deep-link overrides the default-pin heuristic so it never fights
      // an explicit URL.
      defaultPinAppliedRef.current = true;
      deepLinkConsumedRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.delete("movement");
      next.delete("dynamic");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadmap]);

  // When a movement node is pinned, quietly default the Signals filter to
  // that movement so the panel below surfaces the most relevant items.
  // Dynamic pins clear the filter to "All" — dynamics span every
  // movement and shouldn't inherit stale movement filtering. The advisor
  // can still override to any specific movement while a movement pin is
  // active.
  useEffect(() => {
    if (pinnedNode?.type === "movement") setSignalsMovement(pinnedNode.key);
    else setSignalsMovement(null);
  }, [pinnedNode?.type, pinnedNode?.key]);

  useEffect(() => {
    if (!engagementId) return;
    const params = signalsMovement ? `?movement=${signalsMovement}` : "";
    const fetchSignals = () => {
      api.get(`/engagements/${engagementId}/method-signals${params}`)
        .then((r) => setSignals(r.data))
        .catch(() => setSignals({ notes: [], open_tasks: [], upcoming_milestones: [] }));
    };
    fetchSignals();
    // Iter 85.65 · Freshness — the Embrace tab stays mounted across tab
    // switches, so a partner returning from Notes/Tasks/Milestones would
    // previously see cached Signals data until a hard refresh. Refetch
    // whenever the browser tab regains visibility or the window regains
    // focus, so returning to Embrace always reflects the latest canonical
    // sources (notes, tasks, milestones). Cheap; the endpoint is
    // idempotent and small.
    const onFocus = () => fetchSignals();
    const onVisibility = () => { if (document.visibilityState === "visible") fetchSignals(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [engagementId, signalsMovement]);

  // Jump to Briefing Studio carrying inherited client / engagement / dynamic
  // context. Used by the Working Journal composer so the advisor can
  // capture new material without losing where they are.
  const jumpToBriefingStudio = (dynamicKey = null) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client", clientId);
    if (engagementId) params.set("engagement", engagementId);
    if (dynamicKey) params.set("dynamic", dynamicKey);
    navigate(`/notes?${params.toString()}`);
  };

  const suggestEmphasis = async () => {
    try {
      const { data } = await api.post(`/engagements/${engagementId}/roadmap/suggest-emphasis`);
      setRoadmap({ ...roadmap, pending_suggestion: data.suggestion });
      toast.success("YadidAi drafted a suggestion — review the reasoning and unverified list below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion unavailable");
    }
  };

  if (!roadmap) {
    return (
      <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="engagement-embrace-panel">
        Loading this engagement's Embrace workspace…
      </div>
    );
  }

  const isNodePinned = !!pinnedNode;

  // Roman numerals for the four movements — primary Method navigation.
  const MOVEMENT_ROMANS = [
    { key: "listen",    roman: "I",   framework: "Discover", mindset: "Attend · Notice · Absorb" },
    { key: "reveal",    roman: "II",  framework: "Define",   mindset: "Name · Pattern · Frame" },
    { key: "reimagine", roman: "III", framework: "Develop",  mindset: "Explore · Compose · Test" },
    { key: "guide",     roman: "IV",  framework: "Deliver",  mindset: "Commit · Stage · Shepherd" },
  ];

  return (
    <div
      className="space-y-0 bg-secondary/20 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-6 md:py-10 border-t border-b border-border"
      data-testid="engagement-embrace-panel"
      data-embrace-round="4"
    >
      {/* ═══ Unified Embrace instrument — one continuous card ═══
          Iter 82 · The masthead and §II now share one outer frame so the
          engagement reads as a single strategic dossier instead of a
          "header card + Embrace card" stack. The inner hierarchy is
          preserved verbatim (masthead → hairline divider → §II rubric →
          emphasis controls → movement index → emphasis trail →
          Reflection Canvas → Signals → Next Questions). All existing
          `data-testid`s and interaction logic are unchanged. */}
      <div className="border border-border bg-card" data-testid="embrace-instrument-card">
        {/* § I · Editorial masthead + client cover band */}
        <EmbraceMasthead
          engagement={engagement || { id: engagementId }}
          clientId={clientId}
          roadmap={roadmap}
        />

        {/* Iter 82.4 · Control row lives ABOVE the divider so it reads
            as part of the header / control area, not as an element
            inside the map graphic. The divider below then acts as a
            clear separator between utility controls and the
            conceptual map. */}
        <div className="flex items-center justify-end gap-1.5 px-6 md:px-10 pt-3 pb-4" data-testid="embrace-emphasis-controls">
          <Button
            onClick={suggestEmphasis}
            variant="ghost"
            size="sm"
            className="rounded-none h-7 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-transparent px-2"
            data-testid="embrace-ask-yadidai"
            title="Ask YadidAi to suggest an emphasis"
          >
            <Sparkle size={11} weight="fill" className="mr-1" /> Ask YadidAi
          </Button>
          {canChangeEmphasis && (
            <>
              <span aria-hidden className="text-muted-foreground/40 select-none">·</span>
              <Button
                onClick={onOpenEmphasisDialog}
                variant="ghost"
                size="sm"
                className="rounded-none h-7 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-transparent px-2"
                data-testid="embrace-change-emphasis"
                title="Change primary emphasis"
              >
                Change emphasis
              </Button>
            </>
          )}
        </div>

        {/* Internal divider — no frame restart, just a hairline rule
            separating the header/controls from the Embrace map below. */}
        <div aria-hidden className="mx-6 md:mx-10 border-t border-border" />

        {/* § II · The Yadid Embrace — editorial Method index.
            Iter 82.4 · Controls moved above the divider; the map region
            now starts clean underneath, symmetric and centered. */}
        <div
          ref={mapRegionRef}
          className={isNodePinned ? "px-6 md:px-10 pt-6 pb-4" : "px-6 md:px-10 pt-8 md:pt-10 pb-6"}
          data-testid="embrace-map-region"
          data-map-mode={isNodePinned ? "compact" : "idle"}
        >
        {/* Map zone — bounded internal region with symmetric padding so
            the map has the same compositional weight as the masthead
            and reflection sections. */}
        <div
          className={`${isNodePinned ? "pt-2 pb-6 md:pt-3 md:pb-8" : "pt-4 pb-12 md:pt-6 md:pb-16"} transition-all`}
          data-testid="embrace-map-zone"
        >
          {/* ═══ Continuous dynamic · Connections & Relationships ═══
              Iter 82.2 · Rendered as a DOUBLE-RULE editorial rail
              spanning the full map width, with terminal ticks and a
              plaque-style label centered on the rail. This treatment
              is deliberately unlike ordinary hairline dividers or
              section labels elsewhere in the app — it communicates
              that this dynamic runs continuously across all four
              movements below, without needing explanatory copy. */}
          <button
            type="button"
            onClick={() => onSelectDynamic("connections")}
            data-testid="embrace-dynamic-index-connections"
            aria-pressed={pinnedNode?.type === "dynamic" && pinnedNode?.key === "connections"}
            className="w-full group focus:outline-none relative py-3"
          >
            <div aria-hidden className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col gap-[3px]">
              <span className={`h-px transition-colors ${pinnedNode?.type === "dynamic" && pinnedNode?.key === "connections" ? "bg-brass/70" : "bg-border group-hover:bg-brass/50"}`} />
              <span className={`h-px transition-colors ${pinnedNode?.type === "dynamic" && pinnedNode?.key === "connections" ? "bg-brass/70" : "bg-border group-hover:bg-brass/50"}`} />
            </div>
            <span aria-hidden className={`absolute top-1/2 left-0 -translate-y-1/2 w-px h-2 transition-colors ${pinnedNode?.type === "dynamic" && pinnedNode?.key === "connections" ? "bg-brass/70" : "bg-border group-hover:bg-brass/50"}`} />
            <span aria-hidden className={`absolute top-1/2 right-0 -translate-y-1/2 w-px h-2 transition-colors ${pinnedNode?.type === "dynamic" && pinnedNode?.key === "connections" ? "bg-brass/70" : "bg-border group-hover:bg-brass/50"}`} />
            <div className="relative flex justify-center">
              <span
                className={`relative px-5 md:px-6 bg-card text-[0.75rem] font-mono uppercase transition-colors ${
                  pinnedNode?.type === "dynamic" && pinnedNode?.key === "connections"
                    ? "text-brass"
                    : "text-foreground group-hover:text-brass"
                }`}
                style={{ letterSpacing: "0.36em" }}
              >
                Connections &amp; Relationships
              </span>
            </div>
          </button>

          {/* Four movements — equal-width columns, guaranteed centered
              (place-items) so numeral, movement name and descriptor
              line up on the same vertical axis across all four. Iter
              82.2 · vertical breathing above and below is symmetric
              so the movement row sits centered between the two
              continuous rails. */}
          <div
            className={`grid grid-cols-2 md:grid-cols-4 place-items-center gap-x-6 gap-y-10 ${isNodePinned ? "my-8 md:my-10" : "my-14 md:my-16"}`}
            data-testid="embrace-story-index"
          >
            {MOVEMENT_ROMANS.map(({ key, roman, framework, mindset }) => {
              const isCurrent = roadmap.current_phase === key;
              const isSelected = pinnedNode?.type === "movement" && pinnedNode?.key === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSelectMovement(key)}
                  data-testid={`embrace-story-index-${key}`}
                  aria-pressed={isSelected}
                  aria-current={isCurrent ? "true" : undefined}
                  className={`group text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 relative w-full max-w-[16rem] ${isNodePinned ? "pt-7" : "pt-9"}`}
                >
                  {/* Circle indicator above the roman */}
                  <span
                    aria-hidden
                    className={`absolute top-0 left-1/2 -translate-x-1/2 rounded-full transition-all ${
                      isSelected
                        ? "w-3.5 h-3.5 bg-primary border-2 border-primary shadow-[0_0_0_6px_hsl(var(--primary)/0.14)]"
                        : isCurrent
                        ? "w-3 h-3 bg-brass border border-brass"
                        : "w-3 h-3 bg-card border border-muted-foreground/50 group-hover:border-primary"
                    }`}
                  />
                  <div
                    className={`transition-colors ${
                      isCurrent ? "text-brass" : isSelected ? "text-primary" : "text-brass/60"
                    }`}
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontStyle: "italic",
                      fontSize: isNodePinned ? "0.75rem" : "0.8125rem",
                      letterSpacing: "0.05em",
                      fontWeight: 400,
                    }}
                  >
                    {roman}
                  </div>
                  <div
                    className={`mt-1 transition-colors ${
                      isCurrent ? "text-brass" : isSelected ? "text-foreground" : "text-foreground/90 group-hover:text-primary"
                    } ${!isSelected && !isCurrent && isNodePinned ? "opacity-70" : ""}`}
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontSize: isNodePinned ? "1.0625rem" : "1.625rem",
                      lineHeight: 1.05,
                      fontWeight: 700,
                      letterSpacing: "-0.008em",
                    }}
                  >
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </div>
                  <div className={`text-[0.5938rem] font-mono uppercase tracking-[0.28em] text-muted-foreground mt-1.5 ${isNodePinned && !isSelected && !isCurrent ? "opacity-70" : ""}`}>
                    {framework}
                  </div>
                  {!isNodePinned && (
                    <div
                      className="hidden md:block text-[0.75rem] italic text-muted-foreground/80 mt-2 leading-snug px-1"
                      style={{ fontFamily: "'Merriweather', Georgia, serif" }}
                    >
                      {mindset}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* ═══ Continuous dynamic · Leadership & Storytelling ═══
              Symmetric double-rule rail matching the top dynamic so
              the four movements read as happening WITHIN the two
              continuous dynamics. */}
          <button
            type="button"
            onClick={() => onSelectDynamic("leadership")}
            data-testid="embrace-dynamic-index-leadership"
            aria-pressed={pinnedNode?.type === "dynamic" && pinnedNode?.key === "leadership"}
            className="w-full group focus:outline-none relative py-3"
          >
            <div aria-hidden className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col gap-[3px]">
              <span className={`h-px transition-colors ${pinnedNode?.type === "dynamic" && pinnedNode?.key === "leadership" ? "bg-brass/70" : "bg-border group-hover:bg-brass/50"}`} />
              <span className={`h-px transition-colors ${pinnedNode?.type === "dynamic" && pinnedNode?.key === "leadership" ? "bg-brass/70" : "bg-border group-hover:bg-brass/50"}`} />
            </div>
            <span aria-hidden className={`absolute top-1/2 left-0 -translate-y-1/2 w-px h-2 transition-colors ${pinnedNode?.type === "dynamic" && pinnedNode?.key === "leadership" ? "bg-brass/70" : "bg-border group-hover:bg-brass/50"}`} />
            <span aria-hidden className={`absolute top-1/2 right-0 -translate-y-1/2 w-px h-2 transition-colors ${pinnedNode?.type === "dynamic" && pinnedNode?.key === "leadership" ? "bg-brass/70" : "bg-border group-hover:bg-brass/50"}`} />
            <div className="relative flex justify-center">
              <span
                className={`relative px-5 md:px-6 bg-card text-[0.75rem] font-mono uppercase transition-colors ${
                  pinnedNode?.type === "dynamic" && pinnedNode?.key === "leadership"
                    ? "text-brass"
                    : "text-foreground group-hover:text-brass"
                }`}
                style={{ letterSpacing: "0.36em" }}
              >
                Leadership &amp; Storytelling
              </span>
            </div>
          </button>

          <span data-testid="embrace-dynamic-index" className="sr-only" aria-hidden />

          {/* Emphasis trail lives INSIDE the map zone (Iter 82.1) so
              Connections → 4 movements → Leadership → Emphasis Trail
              share one bounded region with symmetric padding. The
              historic "Idle · tap a movement…" hint has been removed —
              the interaction is intuitive and the masthead already
              surfaces current emphasis; the trail chip strip below
              reads as the natural anchor for movement history. */}
          <EmphasisHistoryStrip history={roadmap.emphasis_history || []} />
        </div>{/* ← map zone ends */}

        {/* ═══ Selected phase detail panel — CHILD of §II ═══
            Feb 2026 · Iter 58 — The canvas mounts here, immediately
            below the four-phase index + emphasis trail, INSIDE the
            §II map region. Its presence toggles with `pinnedNode` but
            its DOM location is fixed: selecting/deselecting a phase
            never reorders §II or §III and never pushes anything above
            them. The `data-testid="embrace-canvas-section"` wrapper is
            kept so any deep-link/existing tests continue to resolve
            without depending on a top-level DOM position. */}
        {pinnedNode && (
          <div
            data-testid="embrace-canvas-section"
            className="border-t border-border pt-6 md:pt-8"
          >
            <ReflectionCanvasArticle
              pinnedNode={pinnedNode}
              roadmap={roadmap}
              setRoadmap={setRoadmap}
              engagementId={engagementId}
              engagement={engagement}
              clientId={clientId}
              currentUser={authUser}
              signals={signals}
              signalsMovement={signalsMovement}
              setSignalsMovement={setSignalsMovement}
              onClose={() => setPinnedNode(null)}
              onOpenBriefing={jumpToBriefingStudio}
            />
          </div>
        )}
        </div>
      </div>{/* ← unified Embrace instrument card ends here */}

      {/* ═══ Supporting content — suggestions, drift nudges ═══
          Non-blocking notifications live at the bottom so they don't crowd
          the primary Method index + Reflection Canvas surface up top. */}
      {roadmap.pending_suggestion && (
        <PhaseSuggestionCard
          engagementId={engagementId}
          suggestion={roadmap.pending_suggestion}
          onApproved={(fresh) => setRoadmap(fresh)}
          onDismissed={() => setRoadmap({ ...roadmap, pending_suggestion: null })}
        />
      )}
      <DriftNudgeCard
        engagementId={engagementId}
        roadmap={roadmap}
        onChangeRequested={onOpenEmphasisDialog}
        onUpdated={(fresh) => setRoadmap(fresh)}
      />
    </div>
  );
}
